import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react';
import * as React from 'react';
import { AiFillCloseCircle } from 'react-icons/ai';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { Form, Popover, TextInput } from '@lon/mathnation-suit/components';
import { useCreatePasswordRecoveryRequestMutation } from '@lon/mathnation-suit/requests';
import { getValidation } from './duck';

interface ForgotPasswordProps {
  onBack(): void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const [status, setStatus] = React.useState<'success' | 'error' | null>(null);
  const [createRequest] = useCreatePasswordRecoveryRequestMutation();

  return (
    <Form
      {...getValidation()}
      onSubmit={({ email }) =>
        createRequest({ variables: { email } })
          .then(() => {
            setStatus('success');
          })
          .catch(() => {
            setStatus('error');
          })
      }
    >
      {({ formState }) => (
        <>
          {status === null && (
            <>
              <Text as="h2" fontSize="lg" fontWeight="bold" mb={6}>
                Password Reset
              </Text>

              <Box cursor="default" mb={4}>
                <Popover
                  title={
                    <Badge variant="outline" colorScheme="orange">
                      Info
                    </Badge>
                  }
                  body={
                    <Text>
                      Password Reset function only works if your district has
                      provided an email address. If you do not have an email
                      address associated with your account, contact your System
                      Administrator to reset your password.
                    </Text>
                  }
                >
                  <Text as="span">Enter your </Text>
                  <PopoverTrigger>
                    <Text as="span" textDecoration="underline" cursor="pointer">
                      user account's email
                    </Text>
                  </PopoverTrigger>
                  <Text as="span">
                    {' '}
                    address and we will send you a password reset link.
                  </Text>
                </Popover>
              </Box>

              <TextInput
                isRequired
                name="email"
                label="Email Address"
                placeholder="Enter your email"
              />

              <Flex mt={4} justifyContent="space-between">
                <Button variant="outline" colorScheme="blue" onClick={onBack}>
                  Return
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={formState.isSubmitting}
                >
                  Send Me Link
                </Button>
              </Flex>
            </>
          )}
          {status === 'success' && (
            <VStack>
              <Icon as={IoCheckmarkCircle} color="green.500" boxSize={8} />
              <Text color="green.500" mb={6}>
                Reset link has been sent to the email provided.
              </Text>
              <Text>Check your email.</Text>
              <Text textAlign="center">
                If you have not received this email within a few minutes, check
                your spam folder or contact System Administrator.
              </Text>
              <Flex mt={6}>
                <Button colorScheme="blue" onClick={onBack}>
                  Return to Log In
                </Button>
              </Flex>
            </VStack>
          )}
          {status === 'error' && (
            <VStack>
              <Icon as={AiFillCloseCircle} color="red.500" boxSize={8} />
              <Text color="red.500" mb={6}>
                Sorry, your credentials are not valid.
              </Text>
              <Text textAlign="center">
                Try other email or contact your System Administrator.
              </Text>
              <Flex mt={6}>
                <Button colorScheme="blue" onClick={onBack}>
                  Return to Log In
                </Button>
              </Flex>
            </VStack>
          )}
        </>
      )}
    </Form>
  );
};

export default ForgotPassword;
